/**
 * The screens, and how an incoming link names one.
 *
 * This lives apart from `main.ts` for one reason: `main.ts` ends in
 * `void start()`, so it cannot be imported by a test without booting the whole
 * workspace against a DOM that is not there. The rule that decides where a
 * stranger's link lands somebody is worth testing on its own.
 */

export type View =
  | "overview"
  | "requirements"
  | "financial"
  | "documents"
  | "risk"
  | "ask"
  | "together"
  | "submission"
  | "after"
  | "account";

/**
 * Every view in the design, in nav order. Seven of the ten have no data source
 * yet and render the design filled with clearly-marked example content — see
 * screens.ts and ADR-0038. Keeping them in ONE list means a screen cannot be
 * routed without also being titled, or titled without being routed.
 */
export const VIEWS: View[] = [
  "overview",
  "requirements",
  "financial",
  "documents",
  "risk",
  "ask",
  "together",
  "submission",
  "after",
  "account",
];

/**
 * Where a fragment lands you.
 *
 * There was no hash routing at all until now, and `startPayment.ts` had been
 * sending Stripe to `/app/#billing` — a fragment nothing read, naming a view
 * that does not exist, because billing is a section *inside* Account. Even
 * somebody who got past the redirect described in `session.ts` would have
 * arrived at Overview wondering where their card had gone.
 *
 * AN ALLOWLIST, not `hash as View`. The value is chosen by whoever wrote the
 * link — a Stripe return URL today, an email tomorrow — so it names a screen we
 * recognise or it does nothing at all.
 *
 * `billing` is kept as a name of its own rather than rewritten to `account`.
 * It is a published return URL held in Stripe's configuration, and a link
 * somebody else's system holds should not have to know how we happen to
 * organise our screens this month.
 *
 * The seven example-content screens are deliberately absent: a link that drops
 * a reader straight onto a page of invented content, with no route through the
 * product to explain it, is worse than a link that lands on the overview.
 */
const HASH_VIEWS = new Map<string, View>([
  ["billing", "account"],
  ["account", "account"],
  ["overview", "overview"],
  ["requirements", "requirements"],
  ["financial", "financial"],
]);

/**
 * The view a fragment asks for, or null for one we do not recognise.
 *
 * A `Map`, NOT an object literal, and the test that made it one is in
 * `return-path.test.ts`. Looking a caller's string up in an object reaches the
 * prototype: `#constructor` answered `Object.prototype.constructor`, which is
 * truthy, became `state.view`, and would have been called as a renderer on
 * somebody's dashboard. A `Map` has no prototype chain to walk into.
 */
export function viewFromHash(hash: string): View | null {
  return HASH_VIEWS.get(hash.replace(/^#/, "").toLowerCase()) ?? null;
}
